import {useTranslation} from 'react-i18next';
import {cn} from '@chekinapp/ui';
import {HomeDataProvider} from './HomeDataContext';
import {useHomeData} from './useHomeData';
import {TravelGuidePromoBanner} from './TravelGuidePromoBanner';
import {CheckinHeroStrip} from './CheckinHeroStrip';
import {CheckinChecklist} from './CheckinChecklist';
import {CheckoutChecklist} from './CheckoutChecklist';
import {HomeGuidebooksSection} from './HomeGuidebooksSection';
import {LuggageStorageSection} from 'components/upselling/LuggageBookings';
import {HomeOffers} from './offers/HomeOffers';
import {
  CollapsibleRail,
  HOME_RAIL_GRID,
  RAIL_GRID_TRANSITION,
} from 'components/CollapsibleRail';
import {useCollapsibleRail} from 'hooks/useCollapsibleRail';
import {PAGE_COLUMN, PAGE_TOP_INSET} from 'components/PageLayout';
import {useFirstScreenReady} from 'utils/firstScreen';
import {MobileShell} from './mobile';

function HomeLayout() {
  const {t} = useTranslation();
  const {upsellItems, upsellLoading} = useHomeData();
  const rail = useCollapsibleRail('home');

  const showAside = upsellLoading || upsellItems.length > 0;

  return (
    <div className="mx-auto w-full max-w-[1180px] px-4 py-6 md:px-6 lg:mx-0 lg:max-w-none lg:p-0">
      <div
        className={cn(
          'flex flex-col gap-5 lg:grid lg:gap-0',
          !showAside && 'lg:grid-cols-1',
          showAside && RAIL_GRID_TRANSITION,
          showAside && (rail.isOpen ? HOME_RAIL_GRID.open : HOME_RAIL_GRID.collapsed),
        )}
      >
        <main
          className={cn(
            'contents lg:mx-auto lg:flex lg:w-full lg:flex-col lg:gap-[22px] lg:pb-14',
            PAGE_COLUMN,
            PAGE_TOP_INSET,
          )}
        >
          <CheckinHeroStrip />
          <TravelGuidePromoBanner />
          <CheckinChecklist />
          <CheckoutChecklist />
          <HomeGuidebooksSection />
          <LuggageStorageSection className="order-2 lg:order-none" />
        </main>

        {showAside && (
          <CollapsibleRail
            label={t('offers_for_your_stay')}
            rail="home"
            isOpen={rail.isOpen}
            hasToggled={rail.hasToggled}
            onToggle={rail.toggle}
            collapseLabel={t('collapse_panel')}
            expandLabel={t('expand_panel')}
            className="contents lg:sticky lg:top-[var(--maintenance-banner-height,0px)] lg:flex lg:h-[var(--app-dvh,100dvh)] lg:flex-col lg:self-start lg:bg-[var(--chekin-color-white)] lg:px-[22px] lg:pb-14 lg:pt-[26px]"
            contentClassName="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col lg:gap-[18px] lg:overflow-y-auto"
          >
            <div className="flex flex-col gap-[18px] lg:flex-1">
              <div className="pr-9">
                <h2 className="text-[18px] font-semibold leading-[22px] tracking-[-0.015em] text-[var(--chekin-color-brand-navy)]">
                  {t('offers_for_your_stay')}
                </h2>
                <p className="mt-1 text-[14px] font-medium leading-[21px] text-[var(--chekin-text-secondary)]">
                  {t('rail_offers_description')}
                </p>
              </div>
              <HomeOffers />
            </div>
          </CollapsibleRail>
        )}
      </div>
    </div>
  );
}

export function HomeContent() {
  useFirstScreenReady();

  return (
    <HomeDataProvider>
      <MobileShell>
        <HomeLayout />
      </MobileShell>
    </HomeDataProvider>
  );
}
